'use client';
import React, { useState, useEffect } from 'react';
import { X, Copy, Trophy, UserPlus, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useTranslation } from 'react-i18next';
import { useUserInfo } from '@/app/stores/useUserStore';
import { Tooltip } from 'antd';
import { useShare } from '@/hooks/use-share';
import { useNavigate } from 'react-router-dom';
import { captureReferralLinkCopy, captureReferralShareClick } from '@/lib/analytics/posthog-events';

interface InviteModalProps {
  isOpen: boolean;
  onClose: () => void;
  onInviteClick?: () => void;
}

const InviteModal: React.FC<InviteModalProps> = ({ isOpen, onClose, onInviteClick }) => {
  const { t } = useTranslation();
  const userInfo = useUserInfo();
  const navigate = useNavigate();
  const [isCopied, setIsCopied] = useState(false);
  const [isCopiedCode, setIsCopiedCode] = useState(false);
  const [pointsDetails, setPointsDetails] = useState<any[]>([]);
  const [remainingPoints, setRemainingPoints] = useState(1000);
  const { shareToX, shareToDiscord, shareToTelegram, shareToLine, copyShareContent, copyCode } = useShare();
  // 生成邀请链接
  const generateInviteLink = () => {
    if (!userInfo?.invitationCode) return '';
    return `${process.env.NEXT_PUBLIC_LIVE_URL}/invite/${userInfo?.invitationCode}`;
  };
  // 获取分享参数
  const getShareParams = () => ({
    url: generateInviteLink(),
    code: userInfo?.invitationCode || '',
    username: userInfo?.name || '',
  });

  // 复制邀请码
  const copyInviteCode = async () => {
    if (remainingPoints <= 0) return; // 如果剩余次数为0，禁止复制
    copyCode(generateInviteLink(), {
      onSuccess: () => {
        captureReferralLinkCopy({ source: 'invite_modal', type: 'link' });
        setIsCopied(true);
        setTimeout(() => {
          setIsCopied(false);
        }, 3000);
      },
      onError: error => {
        console.error('复制失败:', error);
      },
    });
  };
  const handleShareTwitter = () => {
    if (remainingPoints <= 0) return; // 如果剩余次数为0，禁止分享

    captureReferralShareClick({ source: 'invite_modal', platform: 'x' });
    shareToX(getShareParams(), {
      onSuccess: () => {
        console.log('分享到X成功');
      },
      onError: error => {
        console.error('分享到X失败:', error);
      },
    });
  };

  const handleLeaderboard = () => {
    // 跳转到排行榜页面
    navigate(`${userInfo?.username}/invite`);
  };

  useEffect(() => {
    if (userInfo) {
      setPointsDetails(userInfo?.pointsUserList || []);
    }
  }, [userInfo]);
  if (!isOpen) return null;

  return (
    <div className="fixed right-0 bottom-12 z-50 flex items-center justify-center z-[50]">
      {/* 弹窗内容 */}
      <div className="bg-popover border border-button-border/50 backdrop-blur-sm relative rounded-lg p-6 w-[400px] max-w-[90vw] mx-4 shadow-2xl  z-20 overflow-hidden">
        {/* 关闭按钮 */}
        <button onClick={onClose} className="absolute top-4 right-4 text-font-gray hover:text-font-primary transition-colors">
          <X className="w-5 h-5" />
        </button>

        {/* 头部 */}
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 bg-background-1 rounded-full flex items-center justify-center">
            <UserPlus className="w-5 h-5 text-font-primary" />
          </div>
          <h2 className="text-xl font-bold text-font-primary">{t('invite.inviteFriends')}</h2>
        </div>

        {/* 邀请链接 */}
        <div className="mb-2">
          <div className="flex items-center gap-2 p-3 bg-background rounded-lg border border-button-border/75">
            <span className="flex-1 text-xs text-font-primary/80 truncate">{generateInviteLink()}</span>
            <button
              className={`p-1 rounded transition-colors ${
                remainingPoints <= 0
                  ? 'opacity-50 cursor-not-allowed'
                  : 'hover:bg-background/70 cursor-pointer text-font-gray hover:text-font-primary'
              }`}
              onClick={() => copyInviteCode()}
              disabled={remainingPoints <= 0}
            >
              {isCopied ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
            </button>
          </div>
        </div>
        <div className="mb-2">
          <div className="flex items-center gap-2 px-3 py-1 bg-background rounded-lg border border-button-border/75">
            <span className="flex-1 text-xs text-font-primary/80 truncate">
              {t('userInvite.inviteCode')} {userInfo?.invitationCode}
            </span>
            <button
              className={`p-1 rounded transition-colors ${
                remainingPoints <= 0
                  ? 'opacity-50 cursor-not-allowed'
                  : 'text-font-gray hover:text-font-primary hover:bg-background/70 cursor-pointer'
              }`}
              onClick={() => {
                copyCode(userInfo?.invitationCode || '', {
                  onSuccess: () => {
                    captureReferralLinkCopy({ source: 'invite_modal', type: 'code' });
                    setIsCopiedCode(true);
                    setTimeout(() => {
                      setIsCopiedCode(false);
                    }, 3000);
                  },
                });
              }}
              disabled={remainingPoints <= 0}
            >
              {isCopiedCode ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
            </button>
            {/* x { remainingPoints } */}
          </div>
        </div>
        {/* 操作按钮 */}
        <div className="flex gap-3">
          <div className="flex items-center gap-1">
            <Tooltip title={t('invite.shareTwitter')}>
              <Button
                onClick={handleShareTwitter}
                variant="outline"
                className={`w-8 h-8 p-0 ${remainingPoints <= 0 ? 'opacity-50 cursor-not-allowed' : ''}`}
                disabled={remainingPoints <= 0}
              >
                <div className="w-3 h-3 co-twitter-icon"></div>
              </Button>
            </Tooltip>
            <Tooltip title={t('invite.shareDiscord')}>
              <Button
                onClick={() => {
                  if (remainingPoints <= 0) return;
                  captureReferralShareClick({ source: 'invite_modal', platform: 'discord' });
                  shareToDiscord(getShareParams());
                }}
                variant="outline"
                className={`w-8 h-8 p-0 ${remainingPoints <= 0 ? 'opacity-50 cursor-not-allowed' : ''}`}
                disabled={remainingPoints <= 0}
              >
                <div className="w-3 h-3 co-discord-icon"></div>
              </Button>
            </Tooltip>
            <Tooltip title={t('invite.shareTelegram')}>
              <Button
                onClick={() => {
                  if (remainingPoints <= 0) return;
                  captureReferralShareClick({ source: 'invite_modal', platform: 'telegram' });
                  shareToTelegram(getShareParams());
                }}
                variant="outline"
                className={`w-8 h-8 p-0 ${remainingPoints <= 0 ? 'opacity-50 cursor-not-allowed' : ''}`}
                disabled={remainingPoints <= 0}
              >
                <div className="w-3 h-3 co-telegram-icon"></div>
              </Button>
            </Tooltip>
            <Tooltip title={t('invite.shareLine')}>
              <Button
                onClick={() => {
                  if (remainingPoints <= 0) return;
                  captureReferralShareClick({ source: 'invite_modal', platform: 'line' });
                  shareToLine(getShareParams());
                }}
                variant="outline"
                className={`w-8 h-8 p-0 ${remainingPoints <= 0 ? 'opacity-50 cursor-not-allowed' : ''}`}
                disabled={remainingPoints <= 0}
              >
                <div className="w-3 h-3 co-line-icon"></div>
              </Button>
            </Tooltip>
          </div>
          <Button onClick={handleLeaderboard} variant="secondary" className="flex-1 h-8">
            <Trophy className="w-4 h-4 mr-2" />
            {t('userInvite.inviteLeaderboard')}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default InviteModal;
